import { useState, useEffect, useCallback, useRef, useMemo, Fragment } from 'react';
import {
    IconSearch,
    IconX,
    IconFileText,
    IconEdit,
    IconPencil,
    IconTrash,
    IconEye,
    IconStar,
    IconStarFilled,
    IconCopy,
    IconFileCode,
    IconForms,
    IconDotsVertical,
    IconChevronLeft,
    IconChevronRight,
    IconChevronDown,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useNavigate } from 'react-router-dom';
import templateSetService from '../../services/templateSetService';
import ConfirmDialog from '../common/ConfirmDialog';
import tagService from '../../services/tagService';
import categoryService from '../../services/categoryService';
import pinnedTemplateSetService from '../../services/pinnedTemplateSetService';
import TooltipButton from '../common/TooltipButton';
import TipBtn from '../common/TipBtn';
import { getHotkeyLabel } from '../../constants/hotkeyConfig';
import DocPreviewPanel from '../user/DocPreviewPanel';
import PreviewValuesModal from '../admin/PreviewValuesModal';
import SelectField from '../common/SelectField';
import PaginationBar from '@/components/common/PaginationBar';
import styles from './TemplateSetTable.module.scss';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

// Simple debounce hook
function useDebouncedValue(value, delay) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return [debounced];
}

// Inline "Free" badge shown beside a template name — enlarged + smooth blink so users spot it easily.
function FreeNameBadge({ t }) {
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span className="inline-flex items-center align-middle ml-1.5 text-[12px] font-extrabold text-emerald-600 bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-400 px-2 py-[2px] rounded-full cursor-help animate-freeBlink">
                    {t('profile.creditFree')}
                </span>
            </TooltipTrigger>
            <TooltipContent>{t('profile.freeTemplateTooltip')}</TooltipContent>
        </Tooltip>
    );
}

// Floating rotated "Free" sticker pinned to the corner of the generate button.
function FreeFloatBadge({ t }) {
    return (
        <span className="pointer-events-none absolute -top-2.5 -right-2 z-20 text-[10px] font-extrabold text-white bg-emerald-500 px-1.5 py-[1px] rounded-full shadow-md shadow-emerald-500/50 animate-freeFloat">
            {t('profile.creditFree')}
        </span>
    );
}

/**
 * Reusable Template Set Table Component
 * Used in both admin and user template pages
 */
export default function TemplateSetTable({
    onEdit,
    onDelete,
    onCopy,
    copyingId = null,
    onGenerate,
    showActions = true,
    isAdmin = false,
    showPinning = false,
    loadingTemplateSetId = null,
    disableGenerate = false,
}) {
    const { t, currentLanguage } = useTranslation();
    const bn = (n) => currentLanguage === 'bn' ? String(n).replace(/\d/g, d => '০১২৩৪৫৬৭৮৯'[d]) : String(n);
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

    // User-facing browser view (warm / cream theme mockup). Admin keeps the existing zinc theme.
    const userView = !isAdmin;
    const USER_GRID = '48px 1fr 130px 80px 70px 80px 230px';
    const warmLangBadge = (lang) => {
        switch (lang) {
            case 'bangla': return 'text-[#2F7D5B] dark:text-emerald-400 bg-[#EAF3EE] dark:bg-emerald-950/30';
            case 'english': return 'text-[#2F5B7D] dark:text-blue-400 bg-[#EAEFF3] dark:bg-blue-950/30';
            case 'mixed': return 'text-[#6B4F91] dark:text-purple-400 bg-[#F0EAF3] dark:bg-purple-950/30';
            default: return 'text-[#8A857B] dark:text-zinc-400 bg-[#F0EFEC] dark:bg-zinc-700';
        }
    };

    const searchInputRef = useRef(null);
    const navigate = useNavigate();

    const [lastPressedDigits, setLastPressedDigits] = useState('');
    const lastPressedTimeRef = useRef(null);

    const [templateSets, setTemplateSets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pinnedTemplateSetIds, setPinnedTemplateSetIds] = useState(new Map());
    const [pinnedFullData, setPinnedFullData] = useState(new Map());

    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch] = useDebouncedValue(searchQuery, 300);
    const [categoryIdFilter, setCategoryIdFilter] = useState('');
    const [languageFilter, setLanguageFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [selectedTags, setSelectedTags] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 12,
        total: 0,
        totalPages: 0
    });

    // Dropdown menu state per row
    const [openMenuId, setOpenMenuId] = useState(null);
    const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
    const menuTriggerRef = useRef(null);

    const sortedTemplateSets = useMemo(() => {
        if (!showPinning || pinnedTemplateSetIds.size === 0) return templateSets;

        if (pagination.page === 1) {
            const pinnedItems = [...pinnedTemplateSetIds.entries()]
                .sort(([, a], [, b]) => a - b)
                .map(([id]) => templateSets.find(ts => ts.id === id) || pinnedFullData.get(id))
                .filter(Boolean);
            const regularItems = templateSets.filter(ts => !pinnedTemplateSetIds.has(ts.id));
            return [...pinnedItems, ...regularItems];
        }

        return templateSets.filter(ts => !pinnedTemplateSetIds.has(ts.id));
    }, [templateSets, pinnedTemplateSetIds, pinnedFullData, showPinning, pagination.page]);

    const [categories, setCategories] = useState([]);
    const [availableTags, setAvailableTags] = useState([]);

    const [previewTemplateSet, setPreviewTemplateSet] = useState(null);
    const [previewDocIndex, setPreviewDocIndex] = useState(0);
    const [previewValuesTarget, setPreviewValuesTarget] = useState(null);
    const [statusTogglingId, setStatusTogglingId] = useState(null);
    const [expandedSetId, setExpandedSetId] = useState(null);
    const [docStampTogglingId, setDocStampTogglingId] = useState(null);
    const [docDeletingId, setDocDeletingId] = useState(null);
    const [docDuplicatingId, setDocDuplicatingId] = useState(null);
    const [confirmDoc, setConfirmDoc] = useState(null); // { templateSet, doc } for delete confirm

    const handleToggleDocStamp = async (templateSet, doc) => {
        if (!isAdmin || docStampTogglingId === doc.id) return;
        const nextStamp = !(doc.metadata?.stamp === true);
        setDocStampTogglingId(doc.id);
        setTemplateSets(prev => prev.map(ts =>
            ts.id !== templateSet.id ? ts : {
                ...ts,
                docs: ts.docs.map(d =>
                    d.id !== doc.id ? d : { ...d, metadata: { ...(d.metadata || {}), stamp: nextStamp } }
                )
            }
        ));
        try {
            await templateSetService.updateTemplateDocContent(doc.id, {
                metadata: { ...(doc.metadata || {}), stamp: nextStamp },
            });
            notifications.show({
                title: t('common.success'),
                message: nextStamp ? 'Stamp enabled' : 'Stamp disabled',
                color: 'green',
                autoClose: 1500,
            });
        } catch (err) {
            setTemplateSets(prev => prev.map(ts =>
                ts.id !== templateSet.id ? ts : {
                    ...ts,
                    docs: ts.docs.map(d => d.id !== doc.id ? d : doc)
                }
            ));
            notifications.show({ title: t('common.error'), message: err.message, color: 'red' });
        } finally {
            setDocStampTogglingId(null);
        }
    };

    const handleDeleteTemplateDoc = async (templateSet, doc) => {
        if (!isAdmin || docDeletingId === doc.id) return;
        setDocDeletingId(doc.id);
        try {
            await templateSetService.deleteTemplateDoc(doc.id);
            setTemplateSets(prev => prev.map(ts =>
                ts.id !== templateSet.id ? ts : {
                    ...ts,
                    docs: ts.docs.filter(d => d.id !== doc.id)
                }
            ));
            notifications.show({ title: t('common.success'), message: `"${doc.title}" deleted`, color: 'green', autoClose: 2000 });
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err.message, color: 'red' });
        } finally {
            setDocDeletingId(null);
        }
    };

    const handleDuplicateTemplateDoc = async (templateSet, doc) => {
        if (!isAdmin || docDuplicatingId === doc.id) return;
        setDocDuplicatingId(doc.id);
        try {
            const res = await templateSetService.duplicateTemplateDoc(doc.id);
            const newDoc = res.data || res;
            setTemplateSets(prev => prev.map(ts =>
                ts.id !== templateSet.id ? ts : {
                    ...ts,
                    docs: [...ts.docs, newDoc]
                }
            ));
            notifications.show({ title: t('common.success'), message: `"${doc.title}" duplicated`, color: 'green', autoClose: 2000 });
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err.message, color: 'red' });
        } finally {
            setDocDuplicatingId(null);
        }
    };

    const handleToggleStatus = async (templateSet) => {
        if (!isAdmin || statusTogglingId === templateSet.id) return;
        const nextStatus = !(templateSet.status === true);
        setStatusTogglingId(templateSet.id);
        setTemplateSets(prev => prev.map(ts =>
            ts.id === templateSet.id ? { ...ts, status: nextStatus } : ts
        ));
        try {
            await templateSetService.updateTemplateSet(templateSet.id, { status: nextStatus });
            notifications.show({
                title: t('common.success'),
                message: nextStatus ? t('templateSets.statusPublished') : t('templateSets.statusDraft'),
                color: 'green',
                autoClose: 1500,
            });
        } catch (err) {
            setTemplateSets(prev => prev.map(ts =>
                ts.id === templateSet.id ? { ...ts, status: templateSet.status } : ts
            ));
            notifications.show({
                title: t('common.error'),
                message: err.message || t('templateSets.failedToUpdateStatus'),
                color: 'red',
            });
        } finally {
            setStatusTogglingId(null);
        }
    };

    const languageOptions = [
        { value: '', label: t('templateSets.allLanguages') },
        { value: 'bangla', label: t('templateSets.bangla') },
        { value: 'english', label: t('templateSets.english') },
        { value: 'mixed', label: t('templateSets.mixed') }
    ];

    const statusOptions = [
        { value: '', label: t('templateSets.allStatus') },
        { value: 'draft', label: t('templateSets.draft') },
        { value: 'published', label: t('templateSets.published') }
    ];

    const fetchTemplateSets = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...(debouncedSearch && { search: debouncedSearch }),
                ...(categoryIdFilter && { categoryId: parseInt(categoryIdFilter) }),
                ...(languageFilter && { language: languageFilter }),
                ...(statusFilter && { status: statusFilter }),
                ...(selectedTags.length > 0 && { tagIds: selectedTags.join(',') })
            };

            const response = await templateSetService.getAllTemplateSets(params);

            setTemplateSets(response.data || []);
            setPagination(prev => ({
                ...prev,
                total: response.pagination?.total || 0,
                totalPages: response.pagination?.totalPages || 0
            }));
        } catch (error) {
            notifications.show({
                title: t('common.error'),
                message: t('templateSets.failedToLoad') || error.message,
                color: 'red'
            });
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, debouncedSearch, categoryIdFilter, languageFilter, statusFilter, selectedTags]);

    const loadTags = async () => {
        try {
            const response = await tagService.getAllTags();
            setAvailableTags((response.data || []).map(tag => ({
                value: tag.id.toString(),
                label: tag.name
            })));
        } catch (error) {
            console.error('Failed to load tags:', error);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await categoryService.getAllCategories();
            const categoryData = response.data || [];
            setCategories([
                { value: '', label: t('templateSets.allCategories') },
                ...categoryData.map(category => ({
                    value: category.id.toString(),
                    label: category.name
                }))
            ]);
        } catch (error) {
            console.error('Failed to load categories:', error);
        }
    };

    const loadPinnedTemplateSets = async () => {
        if (!showPinning) return;

        try {
            const response = await pinnedTemplateSetService.getPinnedTemplateSets();
            const items = response.data || [];
            const pinnedMap = new Map(items.map(pt => [pt.templateSetId, pt.order]));
            const dataMap = new Map(
                items.filter(pt => pt.templateSet).map(pt => [pt.templateSetId, { id: pt.templateSetId, ...pt.templateSet }])
            );
            setPinnedTemplateSetIds(pinnedMap);
            setPinnedFullData(dataMap);
        } catch (error) {
            console.error('Failed to load pinned template sets:', error);
        }
    };

    const handleTogglePin = async (templateSetId) => {
        try {
            const response = await pinnedTemplateSetService.togglePin(templateSetId);
            const isPinned = response?.data?.isPinned ?? false;

            if (isPinned) {
                const fullTs = templateSets.find(ts => ts.id === templateSetId);
                if (fullTs) {
                    setPinnedFullData(prev => new Map(prev).set(templateSetId, fullTs));
                }
                setPinnedTemplateSetIds(prev => new Map(prev).set(templateSetId, Infinity));
                setPagination(prev => ({ ...prev, page: 1 }));
            } else {
                setPinnedFullData(prev => {
                    const m = new Map(prev);
                    m.delete(templateSetId);
                    return m;
                });
                setPinnedTemplateSetIds(prev => {
                    const m = new Map(prev);
                    m.delete(templateSetId);
                    return m;
                });
            }

            notifications.show({
                title: t('common.success'),
                message: (isPinned ? t('templateSets.templateSetPinned') : t('templateSets.templateSetUnpinned')) || response.message,
                color: 'green'
            });
        } catch (error) {
            console.error('Toggle pin error:', error);
            notifications.show({
                title: t('common.error'),
                message: t('templateSets.failedToUpdatePin') || error.message,
                color: 'red'
            });
        }
    };

    useEffect(() => {
        fetchTemplateSets();
        loadTags();
        loadCategories();
        loadPinnedTemplateSets();
    }, [fetchTemplateSets]);

    useEffect(() => {
        if (pagination.page !== 1) {
            setPagination(prev => ({ ...prev, page: 1 }));
        }
    }, [debouncedSearch, categoryIdFilter, languageFilter, statusFilter, selectedTags]);

    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
                return;
            }

            const key = e.key;
            const now = Date.now();

            if ((e.ctrlKey || e.metaKey) && e.shiftKey && (key === 'f' || key === 'F')) {
                e.preventDefault();
                const searchEl = searchInputRef.current;
                if (searchEl) {
                    if (document.activeElement === searchEl) {
                        searchEl.blur();
                    } else {
                        searchEl.focus();
                        if (searchEl.select) searchEl.select();
                    }
                }
                return;
            }

            if ((e.ctrlKey || e.metaKey) && e.shiftKey && key === 'ArrowRight') {
                e.preventDefault();
                if (pagination.page < pagination.totalPages) {
                    setPagination(prev => ({ ...prev, page: prev.page + 1 }));
                }
                return;
            }

            if ((e.ctrlKey || e.metaKey) && e.shiftKey && key === 'ArrowLeft') {
                e.preventDefault();
                if (pagination.page > 1) {
                    setPagination(prev => ({ ...prev, page: prev.page - 1 }));
                }
                return;
            }

            if (lastPressedTimeRef.current && now - lastPressedTimeRef.current > 1000) {
                setLastPressedDigits('');
            }

            if (/^[1-9]$/.test(key)) {
                const nextDigits = `${lastPressedDigits || ''}${key}`;
                setLastPressedDigits(nextDigits);
                lastPressedTimeRef.current = now;
                return;
            }

            if (lastPressedDigits && lastPressedTimeRef.current && (now - lastPressedTimeRef.current) < 1000) {
                const indexNumber = parseInt(lastPressedDigits, 10);
                const index = indexNumber - 1;

                if (key.toLowerCase() === 'g') {
                    e.preventDefault();
                    if (!isAdmin && sortedTemplateSets[index] && onGenerate) {
                        onGenerate(sortedTemplateSets[index]);
                    }
                    setLastPressedDigits('');
                } else if (key.toLowerCase() === 'p') {
                    e.preventDefault();
                    if (showPinning && sortedTemplateSets[index]) {
                        handleTogglePin(sortedTemplateSets[index].id);
                    }
                    setLastPressedDigits('');
                }
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [lastPressedDigits, sortedTemplateSets, isAdmin, onGenerate, showPinning, pagination.page, pagination.totalPages]);

    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, page }));
    };

    const handleClearFilters = () => {
        setSearchQuery('');
        setCategoryIdFilter('');
        setLanguageFilter('');
        setStatusFilter('');
        setSelectedTags([]);
    };

    const hasActiveFilters = searchQuery || categoryIdFilter || languageFilter || statusFilter || selectedTags.length > 0;

    const getLanguageBadgeClass = (language) => {
        switch (language) {
            case 'bangla': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
            case 'english': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
            case 'mixed': return 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400';
            default: return 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400';
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        if (!openMenuId) return;
        const handleClick = () => setOpenMenuId(null);
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [openMenuId]);

    const tableHeaders = (
        <thead className="border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50">
            <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 dark:text-zinc-400 hidden sm:table-cell w-14">#</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 dark:text-zinc-400">{t('common.name')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 dark:text-zinc-400 hidden sm:table-cell">{t('templateSets.category')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 dark:text-zinc-400 hidden md:table-cell">{t('templateSets.language')}</th>
                {isAdmin && <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 dark:text-zinc-400 hidden md:table-cell">{t('templateSets.tags')}</th>}
                <th className="px-4 py-3 text-center text-xs font-semibold text-zinc-600 dark:text-zinc-400 hidden lg:table-cell">{isAdmin ? t('templateSets.placeholders') : t('templateSets.fields')}</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-zinc-600 dark:text-zinc-400 hidden lg:table-cell">{isAdmin ? t('templateSets.status') : t('templateSets.documents')}</th>
                {showActions && <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 dark:text-zinc-400">{t('common.actions')}</th>}
            </tr>
        </thead>
    );

    return (
        <>
            {userView && (
                <>
                    {/* ===== Warm browser theme (user view) ===== */}
                    {/* Search */}
                    <div className="flex items-center gap-2.5 h-12 px-4 border border-[#E9E6E0] dark:border-zinc-700 rounded-xl bg-white dark:bg-zinc-900 mb-4">
                        <IconSearch size={18} stroke={1.8} className="text-[#9C988F] dark:text-zinc-400 flex-shrink-0" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder={t('templateSets.searchPlaceholder')}
                            className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-[#1C1B19] dark:text-zinc-100 placeholder:text-[#9C988F] dark:placeholder:text-zinc-400"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery ? (
                            <button
                                className="p-1 rounded text-[#9C988F] dark:text-zinc-400 hover:text-[#1C1B19] dark:hover:text-zinc-100 transition-colors"
                                onClick={() => setSearchQuery('')}
                            >
                                <IconX size={14} />
                            </button>
                        ) : (
                            <span className="text-xs text-[#A8A294] dark:text-zinc-400 border border-[#ECE9E3] dark:border-zinc-700 rounded-md px-2 py-0.5 hidden sm:inline">
                                Ctrl + K
                            </span>
                        )}
                    </div>

                    {/* Filters */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 mb-5">
                        <div>
                            <label className="block text-xs text-[#6E6A63] dark:text-zinc-300 mb-1.5">{t('documents.filterByTag')}</label>
                            <SelectField
                                value={selectedTags[0] || ''}
                                onChange={(v) => setSelectedTags(v ? [v] : [])}
                                options={[{ value: '', label: t('templateSets.filterByTags') }, ...availableTags]}
                                triggerClassName="h-11 px-3.5 border border-[#E9E6E0] dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900 text-[13.5px] text-[#1C1B19] dark:text-zinc-100"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-[#6E6A63] dark:text-zinc-300 mb-1.5">{t('documents.filterByCategory')}</label>
                            <SelectField
                                value={categoryIdFilter}
                                onChange={setCategoryIdFilter}
                                options={categories}
                                triggerClassName="h-11 px-3.5 border border-[#E9E6E0] dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900 text-[13.5px] text-[#1C1B19] dark:text-zinc-100"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-[#6E6A63] dark:text-zinc-300 mb-1.5">{t('documents.filterByLanguage')}</label>
                            <SelectField
                                value={languageFilter}
                                onChange={setLanguageFilter}
                                options={languageOptions}
                                triggerClassName="h-11 px-3.5 border border-[#E9E6E0] dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900 text-[13.5px] text-[#1C1B19] dark:text-zinc-100"
                            />
                        </div>
                    </div>

                    {hasActiveFilters && (
                        <div className="flex items-center justify-end gap-1 -mt-2 mb-4">
                            <button className="p-1 rounded text-[#9C988F] dark:text-zinc-400 hover:text-[#1C1B19] dark:hover:text-zinc-100 transition-colors" onClick={handleClearFilters}>
                                <IconX size={14} />
                            </button>
                            <span className="text-xs text-[#6E6A63] dark:text-zinc-300 cursor-pointer hover:underline" onClick={handleClearFilters}>
                                {t('common.clearAllFilters')}
                            </span>
                        </div>
                    )}

                    {/* Loading */}
                    {loading && (
                        <div className="border border-[#ECE9E3] dark:border-zinc-700 rounded-2xl overflow-hidden bg-white dark:bg-zinc-900">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <div key={i} className="flex items-center gap-3 px-[18px] py-4 border-b border-[#F4F2EE] dark:border-zinc-800">
                                    <div className="animate-pulse bg-[#F0EFEC] dark:bg-zinc-700 rounded h-3 w-5" />
                                    <div className="flex-1 flex flex-col gap-2">
                                        <div className="animate-pulse bg-[#F0EFEC] dark:bg-zinc-700 rounded h-3.5 w-2/5" />
                                        <div className="animate-pulse bg-[#F0EFEC] dark:bg-zinc-700 rounded h-2.5 w-3/5" />
                                    </div>
                                    <div className="animate-pulse bg-[#F0EFEC] dark:bg-zinc-700 rounded-md h-5 w-16 hidden md:block" />
                                    <div className="animate-pulse bg-primary/20 rounded-[9px] h-8 w-24" />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Empty */}
                    {!loading && templateSets.length === 0 && (
                        <div className="border border-[#ECE9E3] dark:border-zinc-700 rounded-2xl bg-white dark:bg-zinc-900 p-12 flex flex-col items-center gap-4">
                            <IconFileText size={48} strokeWidth={1.5} className="text-[#C3BEB4] dark:text-zinc-500" />
                            <p className="text-lg font-medium text-[#6E6A63] dark:text-zinc-300">
                                {hasActiveFilters ? t('templateSets.noTemplateSetsFound') : t('templateSets.noTemplateSetsAvailable')}
                            </p>
                            <p className="text-sm text-[#9C988F] dark:text-zinc-400">
                                {hasActiveFilters ? t('templateSets.tryAdjustingFilters') : t('templateSets.templateSetsWillAppear')}
                            </p>
                        </div>
                    )}

                    {/* List */}
                    {!loading && templateSets.length > 0 && (
                        <>
                            {/* Mobile cards */}
                            <div className="sm:hidden flex flex-col gap-2 mb-4">
                                {sortedTemplateSets.map((templateSet, index) => (
                                    <div
                                        key={templateSet.id}
                                        className="bg-white dark:bg-zinc-900 rounded-xl border border-[#ECE9E3] dark:border-zinc-700 p-3 flex flex-col gap-2 active:scale-[0.99] transition-transform cursor-pointer"
                                        onClick={() => !disableGenerate && onGenerate?.(templateSet)}
                                    >
                                        <div className="flex items-start gap-2">
                                            {showPinning && (
                                                <button
                                                    className={`flex-shrink-0 mt-0.5 transition-colors ${pinnedTemplateSetIds.has(templateSet.id) ? 'text-yellow-500' : 'text-[#C3BEB4] dark:text-zinc-500'}`}
                                                    onClick={(e) => { e.stopPropagation(); handleTogglePin(templateSet.id); }}
                                                >
                                                    {pinnedTemplateSetIds.has(templateSet.id) ? <IconStarFilled size={15} /> : <IconStar size={15} />}
                                                </button>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-[#1C1B19] dark:text-zinc-100 leading-snug">{templateSet.name}{templateSet.isFree && <FreeNameBadge t={t} />}</p>
                                                {templateSet.description && (
                                                    <p className="text-xs text-[#9C988F] dark:text-zinc-400 mt-0.5">{templateSet.description}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                            <span className="relative flex-1 flex">
                                            {templateSet.isFree && <FreeFloatBadge t={t} />}
                                            {disableGenerate ? (
                                                <span className="w-full text-center px-3 py-1.5 text-sm rounded-[9px] bg-primary/50 text-white font-semibold inline-flex items-center justify-center gap-1.5 opacity-60 cursor-not-allowed">
                                                    <IconPencil size={14} />
                                                    {t('templateSets.generate')}
                                                </span>
                                            ) : (
                                                <button
                                                    className="w-full px-3 py-1.5 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-[9px] inline-flex items-center justify-center gap-1.5 transition-colors disabled:opacity-60"
                                                    onClick={() => onGenerate?.(templateSet)}
                                                    disabled={loadingTemplateSetId === templateSet.id}
                                                >
                                                    {loadingTemplateSetId === templateSet.id ? (
                                                        <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                    ) : (
                                                        <IconPencil size={14} />
                                                    )}
                                                    {t('templateSets.generate')}
                                                </button>
                                            )}
                                            </span>
                                            <button
                                                className="px-3 py-1.5 border border-[#ECE9E3] dark:border-zinc-700 text-[#6E6A63] dark:text-zinc-300 text-sm rounded-[9px] inline-flex items-center gap-1.5 hover:bg-[#FAF9F6] dark:hover:bg-zinc-800 transition-colors whitespace-nowrap"
                                                onClick={() => { setPreviewTemplateSet(templateSet); setPreviewDocIndex(0); }}
                                            >
                                                <IconEye size={14} />
                                                {t('templateSets.previewDekhun')}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Desktop grid table */}
                            <div className="hidden sm:block border border-[#ECE9E3] dark:border-zinc-700 rounded-2xl overflow-hidden bg-white dark:bg-zinc-900">
                                <div className="overflow-x-auto">
                                    <div className="min-w-[900px]">
                                        {/* Header */}
                                        <div
                                            className="grid items-center gap-3 px-[18px] py-3.5 bg-[#FAF9F6] dark:bg-zinc-800 border-b border-[#ECE9E3] dark:border-zinc-700 text-xs font-semibold text-[#8A857B] dark:text-zinc-400"
                                            style={{ gridTemplateColumns: USER_GRID }}
                                        >
                                            <div className="text-center">#</div>
                                            <div>{t('common.name')}</div>
                                            <div>{t('templateSets.category')}</div>
                                            <div>{t('templateSets.language')}</div>
                                            <div className="text-center">{t('templateSets.fields')}</div>
                                            <div className="text-center">{t('templateSets.documents')}</div>
                                            <div>{t('common.actions')}</div>
                                        </div>
                                        {/* Rows */}
                                        {sortedTemplateSets.map((templateSet, index) => (
                                            <div
                                                key={templateSet.id}
                                                className="grid items-center gap-3 px-[18px] py-4 border-b border-[#F4F2EE] dark:border-zinc-800 last:border-b-0 hover:bg-[#FCFBF9] dark:hover:bg-zinc-800 transition-colors"
                                                style={{ gridTemplateColumns: USER_GRID }}
                                            >
                                                <div className="text-center text-[13px] text-[#A8A294] dark:text-zinc-400">{bn(index + 1)}</div>
                                                <div className="min-w-0 flex items-start gap-2.5">
                                                    {showPinning && (
                                                        <TipBtn
                                                            tip={`${index + 1} + P`}
                                                            className={`flex-shrink-0 mt-0.5 transition-colors ${pinnedTemplateSetIds.has(templateSet.id) ? 'text-yellow-500' : 'text-[#C3BEB4] dark:text-zinc-500 hover:text-[#E2702E] dark:hover:text-orange-400'}`}
                                                            onClick={() => handleTogglePin(templateSet.id)}
                                                        >
                                                            {pinnedTemplateSetIds.has(templateSet.id) ? <IconStarFilled size={15} /> : <IconStar size={15} />}
                                                        </TipBtn>
                                                    )}
                                                    <div className="min-w-0">
                                                        <div className="text-sm font-semibold text-[#1C1B19] dark:text-zinc-100 truncate">{templateSet.name}{templateSet.isFree && <FreeNameBadge t={t} />}</div>
                                                        {templateSet.description && (
                                                            <div className="text-xs text-[#9C988F] dark:text-zinc-400 mt-[3px] line-clamp-2">
                                                                {templateSet.description}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div>
                                                    <span className="inline-block text-[11.5px] font-semibold text-[#B5862B] dark:text-amber-400 bg-[#F7F0E0] dark:bg-orange-950/30 px-2.5 py-[3px] rounded-md whitespace-nowrap">
                                                        {templateSet.category?.name || t('templateSets.uncategorized')}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className={`inline-block text-[11.5px] font-semibold px-2.5 py-[3px] rounded-md whitespace-nowrap ${warmLangBadge(templateSet.language)}`}>
                                                        {templateSet.language ? t(`templateSets.${templateSet.language}`) : t('templateSets.notSet')}
                                                    </span>
                                                </div>
                                                <div className="text-center text-sm text-[#1C1B19] dark:text-zinc-100">
                                                    {bn(templateSet.placeholders?.filter(p => p.type !== 'Steps').length || 0)}
                                                </div>
                                                <div className="text-center text-sm text-[#1C1B19] dark:text-zinc-100">
                                                    {bn(templateSet.docs?.length || 0)}
                                                </div>
                                                <div className="flex items-center gap-2.5">
                                                    <span className="relative inline-flex">
                                                    {templateSet.isFree && <FreeFloatBadge t={t} />}
                                                    {disableGenerate ? (
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary/50 text-white rounded-[9px] text-[12.5px] font-semibold opacity-60 cursor-not-allowed whitespace-nowrap">
                                                            <IconPencil size={13} />
                                                            {t('templateSets.generate')}
                                                        </span>
                                                    ) : (
                                                        <button
                                                            className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary hover:bg-primary/90 text-white rounded-[9px] text-[12.5px] font-semibold whitespace-nowrap transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                                            onClick={() => onGenerate?.(templateSet)}
                                                            disabled={loadingTemplateSetId === templateSet.id}
                                                        >
                                                            {loadingTemplateSetId === templateSet.id ? (
                                                                <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                            ) : (
                                                                <IconPencil size={13} />
                                                            )}
                                                            {t('templateSets.generate')}
                                                        </button>
                                                    )}
                                                    </span>
                                                    <button
                                                        className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#6E6A63] dark:text-zinc-300 hover:text-[#1C1B19] dark:hover:text-zinc-100 whitespace-nowrap transition-colors"
                                                        onClick={() => { setPreviewTemplateSet(templateSet); setPreviewDocIndex(0); }}
                                                    >
                                                        <IconEye size={14} />
                                                        {t('templateSets.previewDekhun')}
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Pagination */}
                            {templateSets.length > 0 && (
                                <div className="mt-6">
                                    <PaginationBar
                                        page={pagination.page}
                                        totalPages={pagination.totalPages}
                                        total={pagination.total}
                                        pageSize={pagination.limit}
                                        currentCount={templateSets.length}
                                        onPage={handlePageChange}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </>
            )}

            {!userView && (<>
            {/* Search and Filters */}
            <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-3 sm:p-4 mb-3 sm:mb-6">
                <div className="flex flex-col gap-2">
                    {/* Search row */}
                    <div className="relative">
                        <IconSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder={t('templateSets.searchPlaceholder')}
                            className="filter-field filter-search"
                            style={{ paddingRight: 80 }}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery ? (
                            <button
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-500"
                                onClick={() => setSearchQuery('')}
                            >
                                <IconX size={14} />
                            </button>
                        ) : (
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-400 pointer-events-none hidden sm:inline">
                                Ctrl + K
                            </span>
                        )}
                    </div>

                    {/* Filters row */}
                    <div className={`grid gap-2 grid-cols-2 pt-1 ${isAdmin ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
                        <div className="flex flex-col gap-1">
                            <label className="text-xs text-zinc-500 dark:text-zinc-400 font-medium px-1">{t('documents.filterByTag')}</label>
                            <SelectField
                                value={selectedTags[0] || ''}
                                onChange={(v) => setSelectedTags(v ? [v] : [])}
                                options={[{ value: '', label: t('templateSets.filterByTags') }, ...availableTags]}
                            />
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-xs text-zinc-500 dark:text-zinc-400 font-medium px-1">{t('documents.filterByCategory')}</label>
                            <SelectField
                                value={categoryIdFilter}
                                onChange={setCategoryIdFilter}
                                options={categories}
                            />
                        </div>

                        <div className="hidden sm:flex flex-col gap-1">
                            <label className="text-xs text-zinc-500 dark:text-zinc-400 font-medium px-1">{t('documents.filterByLanguage')}</label>
                            <SelectField
                                value={languageFilter}
                                onChange={setLanguageFilter}
                                options={languageOptions}
                            />
                        </div>

                        {isAdmin && (
                            <div className="hidden sm:flex flex-col gap-1">
                                <label className="text-xs text-zinc-500 dark:text-zinc-400 font-medium px-1">{t('templateSets.filterByStatus') || 'Status'}</label>
                                <SelectField
                                    value={statusFilter}
                                    onChange={setStatusFilter}
                                    options={statusOptions}
                                />
                            </div>
                        )}
                    </div>

                    {hasActiveFilters && (
                        <div className="flex items-center justify-end gap-1">
                            <button
                                className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-500"
                                onClick={handleClearFilters}
                            >
                                <IconX size={14} />
                            </button>
                            <span
                                className="text-xs text-zinc-500 dark:text-zinc-400 cursor-pointer hover:underline"
                                onClick={handleClearFilters}
                            >
                                {t('common.clearAllFilters')}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Loading State */}
            {loading && (
                <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            {tableHeaders}
                            <tbody>
                                {Array.from({ length: 8 }).map((_, i) => (
                                    <tr key={i} className="border-b border-zinc-100 dark:border-zinc-800">
                                        <td className="px-4 py-4 hidden sm:table-cell">
                                            <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded-full w-7 h-7" />
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex flex-col gap-2">
                                                <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded h-3.5 w-3/5" />
                                                <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded h-2.5 w-2/5" />
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 hidden sm:table-cell">
                                            <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded-full h-5 w-16" />
                                        </td>
                                        <td className="px-4 py-4 hidden md:table-cell">
                                            <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded-full h-5 w-14" />
                                        </td>
                                        {isAdmin && (
                                            <td className="px-4 py-4 hidden md:table-cell">
                                                <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded-full h-5 w-20" />
                                            </td>
                                        )}
                                        <td className="px-4 py-4 hidden lg:table-cell">
                                            <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded h-3 w-5 mx-auto" />
                                        </td>
                                        <td className="px-4 py-4 hidden lg:table-cell">
                                            <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded h-3 w-5 mx-auto" />
                                        </td>
                                        {showActions && (
                                            <td className="px-4 py-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded-lg w-24 h-8" />
                                                    <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 rounded w-20 h-5" />
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Empty State */}
            {!loading && templateSets.length === 0 && (
                <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-12">
                    <div className="flex items-center justify-center">
                        <div className="flex flex-col items-center gap-4">
                            <IconFileText size={48} strokeWidth={1.5} className="text-zinc-400" />
                            <p className="text-lg font-medium text-zinc-500 dark:text-zinc-400">
                                {hasActiveFilters ? t('templateSets.noTemplateSetsFound') : t('templateSets.noTemplateSetsAvailable')}
                            </p>
                            <p className="text-sm text-zinc-500 dark:text-zinc-400">
                                {hasActiveFilters
                                    ? t('templateSets.tryAdjustingFilters')
                                    : t('templateSets.templateSetsWillAppear')}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Template Set Table */}
            {!loading && templateSets.length > 0 && (
                <>
                    {/* Mobile card list (user only) */}
                    {!isAdmin && (
                        <div className="sm:hidden flex flex-col gap-2 mb-4">
                            {sortedTemplateSets.map((templateSet) => (
                                <div
                                    key={templateSet.id}
                                    className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-3 flex flex-col gap-2 active:scale-[0.99] transition-transform cursor-pointer"
                                    onClick={() => !disableGenerate && onGenerate?.(templateSet)}
                                >
                                    <div className="flex items-start gap-2">
                                        {showPinning && (
                                            <button
                                                className={`p-1 rounded flex-shrink-0 mt-0.5 transition-colors ${pinnedTemplateSetIds.has(templateSet.id) ? 'text-yellow-500' : 'text-zinc-300 dark:text-zinc-600'}`}
                                                onClick={(e) => { e.stopPropagation(); handleTogglePin(templateSet.id); }}
                                            >
                                                {pinnedTemplateSetIds.has(templateSet.id) ? <IconStarFilled size={15} /> : <IconStar size={15} />}
                                            </button>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 leading-snug">{templateSet.name}{templateSet.isFree && <FreeNameBadge t={t} />}</p>
                                            {templateSet.description && (
                                                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{templateSet.description}</p>
                                            )}
                                        </div>
                                    </div>
                                    {showActions && (
                                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                            {disableGenerate ? (
                                                <span className="flex-1 text-center px-3 py-1.5 text-sm rounded-lg bg-orange-100 text-orange-400 font-semibold inline-flex items-center justify-center gap-1.5 opacity-50 cursor-not-allowed">
                                                    <IconPencil size={14} />
                                                    {t('templateSets.generate')}
                                                </span>
                                            ) : (
                                                <button
                                                    className="flex-1 px-3 py-1.5 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-lg inline-flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
                                                    onClick={() => onGenerate?.(templateSet)}
                                                    disabled={loadingTemplateSetId === templateSet.id}
                                                >
                                                    {loadingTemplateSetId === templateSet.id ? (
                                                        <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                    ) : (
                                                        <IconPencil size={14} />
                                                    )}
                                                    {t('templateSets.generate')}
                                                </button>
                                            )}
                                            <button
                                                className="px-3 py-1.5 border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 text-sm rounded-lg inline-flex items-center gap-1.5 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors whitespace-nowrap"
                                                onClick={() => { setPreviewTemplateSet(templateSet); setPreviewDocIndex(0); }}
                                            >
                                                <IconEye size={14} />
                                                {t('templateSets.previewDekhun')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Desktop table */}
                    <div className={`rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 ${!isAdmin ? 'hidden sm:block' : ''}`}>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                {tableHeaders}
                                <tbody>
                                    {sortedTemplateSets.map((templateSet, index) => (
                                        <Fragment key={templateSet.id}>
                                            <tr className="border-b border-zinc-100 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                                                <td className="px-4 py-4 hidden sm:table-cell">
                                                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-zinc-100 dark:bg-zinc-800 text-xs font-semibold text-zinc-600 dark:text-zinc-400">
                                                        {bn(index + 1)}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-4">
                                                    <div className="flex items-center gap-2 flex-nowrap">
                                                        {isAdmin && (
                                                            <button
                                                                className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-500 flex-shrink-0"
                                                                onClick={() => setExpandedSetId(prev => prev === templateSet.id ? null : templateSet.id)}
                                                            >
                                                                <IconChevronDown
                                                                    size={15}
                                                                    className={`transition-transform duration-200 ${expandedSetId === templateSet.id ? 'rotate-180' : 'rotate-0'}`}
                                                                />
                                                            </button>
                                                        )}
                                                        {showPinning && (
                                                            <TipBtn
                                                                tip={`${index + 1} + P`}
                                                                className={`p-1 rounded flex-shrink-0 transition-colors ${pinnedTemplateSetIds.has(templateSet.id) ? 'text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-900/20' : 'text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-700'}`}
                                                                onClick={() => handleTogglePin(templateSet.id)}
                                                            >
                                                                {pinnedTemplateSetIds.has(templateSet.id) ? (
                                                                    <IconStarFilled size={16} />
                                                                ) : (
                                                                    <IconStar size={16} />
                                                                )}
                                                            </TipBtn>
                                                        )}
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 leading-snug">{templateSet.name}{templateSet.isFree && <FreeNameBadge t={t} />}</p>
                                                            {templateSet.description && (
                                                                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 line-clamp-1">
                                                                    {templateSet.description.length > 120
                                                                        ? templateSet.description.slice(0, 120) + '…'
                                                                        : templateSet.description}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4 hidden sm:table-cell">
                                                    <span className="px-2.5 py-1 text-xs rounded-full font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 whitespace-nowrap">
                                                        {templateSet.category?.name || t('templateSets.uncategorized')}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-4 hidden md:table-cell">
                                                    <span className={`px-2.5 py-1 text-xs rounded-full font-medium whitespace-nowrap ${getLanguageBadgeClass(templateSet.language)}`}>
                                                        {templateSet.language ? t(`templateSets.${templateSet.language}`) : t('templateSets.notSet')}
                                                    </span>
                                                </td>
                                                {isAdmin && (
                                                    <td className="px-4 py-4 hidden md:table-cell">
                                                        {templateSet.tags && templateSet.tags.length > 0 ? (
                                                            <div className="flex items-center gap-1 flex-wrap">
                                                                {templateSet.tags.slice(0, 2).map((tag) => (
                                                                    <span
                                                                        key={tag.id}
                                                                        className="px-1.5 py-0.5 text-xs rounded-full bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400"
                                                                    >
                                                                        {tag.name}
                                                                    </span>
                                                                ))}
                                                                {templateSet.tags.length > 2 && (
                                                                    <span className="px-1.5 py-0.5 text-xs rounded-full bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
                                                                        +{templateSet.tags.length - 2}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-zinc-500 dark:text-zinc-400">-</p>
                                                        )}
                                                    </td>
                                                )}
                                                <td className="px-4 py-4 hidden lg:table-cell text-center">
                                                    <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                                        {bn(templateSet.placeholders?.filter(p => p.type !== 'Steps').length || 0)}
                                                    </p>
                                                </td>
                                                <td className="px-4 py-4 hidden lg:table-cell text-center">
                                                    {isAdmin ? (
                                                        <TipBtn
                                                            tip={t('templateSets.clickToToggleStatus')}
                                                            className={`px-2 py-0.5 text-xs rounded-full font-medium transition-colors ${templateSet.status === true
                                                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                                : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'
                                                                } ${statusTogglingId === templateSet.id ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
                                                            onClick={() => handleToggleStatus(templateSet)}
                                                        >
                                                            {templateSet.status === true ? t('templateSets.published') : t('templateSets.draft')}
                                                        </TipBtn>
                                                    ) : (
                                                        <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                                            {bn(templateSet.docs?.length || 0)}
                                                        </p>
                                                    )}
                                                </td>
                                                {showActions && (
                                                    <td className="px-4 py-4">
                                                        {isAdmin ? (
                                                            <div className="flex items-center gap-1">
                                                                <TipBtn
                                                                    tip={t('common.edit')}
                                                                    className="p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600 transition-colors"
                                                                    onClick={() => onEdit?.(templateSet)}
                                                                >
                                                                    <IconEdit size={18} />
                                                                </TipBtn>
                                                                {/* Dots menu */}
                                                                <div className="relative" onMouseDown={e => e.stopPropagation()}>
                                                                    <TipBtn
                                                                        tip={t('templateSets.moreActions')}
                                                                        className="p-1.5 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-600 dark:text-zinc-400 transition-colors"
                                                                        onClick={(e) => {
                                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                                            setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                                                                            setOpenMenuId(prev => prev === templateSet.id ? null : templateSet.id);
                                                                        }}
                                                                    >
                                                                        <IconDotsVertical size={18} />
                                                                    </TipBtn>
                                                                    {openMenuId === templateSet.id && (
                                                                        <div style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 9999 }} className="w-56 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-lg py-1">
                                                                            <button
                                                                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50"
                                                                                onClick={() => { onCopy?.(templateSet); setOpenMenuId(null); }}
                                                                                disabled={copyingId === templateSet.id}
                                                                            >
                                                                                {copyingId === templateSet.id ? (
                                                                                    <span className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                                                                                ) : (
                                                                                    <IconCopy size={16} className="text-green-600" />
                                                                                )}
                                                                                {t('common.copy')}
                                                                            </button>
                                                                            <button
                                                                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                                                                                onClick={() => { onDelete?.(templateSet); setOpenMenuId(null); }}
                                                                            >
                                                                                <IconTrash size={16} className="text-red-600" />
                                                                                {t('common.delete')}
                                                                            </button>
                                                                            <button
                                                                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                                                                                onClick={() => { setPreviewValuesTarget(templateSet); setOpenMenuId(null); }}
                                                                            >
                                                                                <IconForms size={16} className="text-teal-600" />
                                                                                {t('templateSets.dummyPreviewValues')}
                                                                            </button>
                                                                            {templateSet.docs?.[0] && (
                                                                                <>
                                                                                    <hr className="border-zinc-200 dark:border-zinc-700 my-1" />
                                                                                    <button
                                                                                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                                                                                        onClick={() => { window.open(`/admin/lexical-editor/${templateSet.id}/${templateSet.docs[0].id}`, '_blank'); setOpenMenuId(null); }}
                                                                                    >
                                                                                        <IconFileCode size={16} className="text-violet-600" />
                                                                                        {t('templateSets.openInLexical')}
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ) : disableGenerate ? (
                                                            <div className="flex items-center gap-3 flex-nowrap">
                                                                <span className="px-3 py-1.5 text-sm rounded-lg bg-orange-100 text-orange-400 font-semibold inline-flex items-center gap-1.5 opacity-50 cursor-not-allowed">
                                                                    <IconPencil size={14} />
                                                                    {t('templateSets.generate')}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-3 flex-nowrap">
                                                                <button
                                                                    className="px-3 py-1.5 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-md inline-flex items-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                                                    onClick={() => onGenerate?.(templateSet)}
                                                                    disabled={loadingTemplateSetId === templateSet.id}
                                                                >
                                                                    {loadingTemplateSetId === templateSet.id ? (
                                                                        <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                                    ) : (
                                                                        <IconPencil size={14} />
                                                                    )}
                                                                    {t('templateSets.generate')}
                                                                </button>
                                                                <button
                                                                    className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-800 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-700 px-2.5 py-1.5 rounded-md transition-colors whitespace-nowrap"
                                                                    onClick={() => { setPreviewTemplateSet(templateSet); setPreviewDocIndex(0); }}
                                                                >
                                                                    <IconEye size={15} />
                                                                    {t('templateSets.previewDekhun')}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                            {/* Expanded docs row */}
                                            {isAdmin && expandedSetId === templateSet.id && (
                                                <tr>
                                                    <td colSpan={20} className="p-0 border-b border-zinc-200 dark:border-zinc-700">
                                                        <div className="px-4 py-3 bg-zinc-50 dark:bg-zinc-800/50 border-b border-zinc-200 dark:border-zinc-700">
                                                            <p className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wide mb-2">
                                                                Documents ({templateSet.docs?.length || 0})
                                                            </p>
                                                            <div className="flex flex-col gap-2">
                                                                {(templateSet.docs || []).length === 0 ? (
                                                                    <p className="text-sm text-zinc-500 dark:text-zinc-400">No documents in this template set.</p>
                                                                ) : (templateSet.docs || []).map(doc => (
                                                                    <div key={doc.id} className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-3">
                                                                        <div className="flex items-start justify-between gap-2 flex-nowrap">
                                                                            <div className="flex items-start gap-2 min-w-0 flex-nowrap">
                                                                                <IconFileText size={14} className="text-zinc-400 flex-shrink-0 mt-0.5" />
                                                                                <div className="min-w-0">
                                                                                    <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">{doc.title}</p>
                                                                                    {doc.description && (
                                                                                        <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{doc.description}</p>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                            <div className="flex items-center gap-2 flex-shrink-0 flex-nowrap">
                                                                                {doc.metadata?.pageSize && (
                                                                                    <span className="px-1.5 py-0.5 text-xs rounded border border-blue-300 text-blue-600 dark:border-blue-700 dark:text-blue-400">{doc.metadata.pageSize}</span>
                                                                                )}
                                                                                {doc.metadata?.margin && (
                                                                                    <span className="px-1.5 py-0.5 text-xs rounded border border-zinc-300 text-zinc-600 dark:border-zinc-600 dark:text-zinc-400">{doc.metadata.margin}</span>
                                                                                )}
                                                                                <div className="h-4 w-px bg-zinc-200 dark:bg-zinc-700" />
                                                                                {docStampTogglingId === doc.id ? (
                                                                                    <div className="flex items-center gap-1">
                                                                                        <span className="w-3 h-3 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
                                                                                        <span className="text-xs text-zinc-500">Saving…</span>
                                                                                    </div>
                                                                                ) : (
                                                                                    <TipBtn
                                                                                        tip="Click to toggle stamp"
                                                                                        className={`px-2 py-0.5 text-xs rounded-full font-medium cursor-pointer transition-colors ${doc.metadata?.stamp === true
                                                                                            ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                                                                                            : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'
                                                                                            }`}
                                                                                        onClick={() => handleToggleDocStamp(templateSet, doc)}
                                                                                    >
                                                                                        {doc.metadata?.stamp === true ? 'Stamp' : 'No Stamp'}
                                                                                    </TipBtn>
                                                                                )}
                                                                                <div className="h-4 w-px bg-zinc-200 dark:bg-zinc-700" />
                                                                                <TipBtn
                                                                                    tip="Open in Lexical editor"
                                                                                    className="p-1 rounded hover:bg-violet-50 dark:hover:bg-violet-900/20 text-violet-600 transition-colors"
                                                                                    onClick={() => window.open(`/admin/lexical-editor/${templateSet.id}/${doc.id}`, '_blank')}
                                                                                >
                                                                                    <IconFileCode size={15} />
                                                                                </TipBtn>
                                                                                <TipBtn
                                                                                    tip="Duplicate this document"
                                                                                    className="p-1 rounded hover:bg-teal-50 dark:hover:bg-teal-900/20 text-teal-600 transition-colors disabled:opacity-50"
                                                                                    disabled={docDuplicatingId === doc.id}
                                                                                    onClick={() => handleDuplicateTemplateDoc(templateSet, doc)}
                                                                                >
                                                                                    {docDuplicatingId === doc.id ? (
                                                                                        <span className="w-3 h-3 border-2 border-teal-500 border-t-transparent rounded-full animate-spin block" />
                                                                                    ) : (
                                                                                        <IconCopy size={15} />
                                                                                    )}
                                                                                </TipBtn>
                                                                                <TipBtn
                                                                                    tip="Delete this document"
                                                                                    className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 transition-colors disabled:opacity-50"
                                                                                    disabled={docDeletingId === doc.id}
                                                                                    onClick={() => setConfirmDoc({ templateSet, doc })}
                                                                                >
                                                                                    {docDeletingId === doc.id ? (
                                                                                        <span className="w-3 h-3 border-2 border-red-500 border-t-transparent rounded-full animate-spin block" />
                                                                                    ) : (
                                                                                        <IconTrash size={15} />
                                                                                    )}
                                                                                </TipBtn>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {templateSets.length > 0 && (
                        <div className="mt-6">
                            <PaginationBar
                                page={pagination.page}
                                totalPages={pagination.totalPages}
                                total={pagination.total}
                                pageSize={pagination.limit}
                                currentCount={templateSets.length}
                                onPage={handlePageChange}
                            />
                        </div>
                    )}
                </>
            )}
            </>)}

            {/* Template Preview Modal — fullscreen */}
            {!!previewTemplateSet && (
                <div className="fixed inset-0 z-[2147483647] flex flex-col bg-white dark:bg-zinc-900">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 dark:border-zinc-700 flex-shrink-0">
                        <div className="flex items-center gap-2 min-w-0">
                            <IconEye size={16} className="flex-shrink-0 text-zinc-500" />
                            <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">
                                {previewTemplateSet?.name}
                            </span>
                        </div>
                        <button
                            className="p-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors flex-shrink-0"
                            onClick={() => setPreviewTemplateSet(null)}
                        >
                            <IconX size={18} />
                        </button>
                    </div>

                    {previewTemplateSet?.docs?.length > 0 ? (
                        <div className="flex-1 flex flex-col overflow-hidden">
                            {previewTemplateSet.docs.length > 1 && (
                                <div className="overflow-x-auto border-b border-zinc-200 dark:border-zinc-700 flex-shrink-0">
                                    <div className="flex gap-1 px-3 py-1.5 flex-nowrap">
                                        {previewTemplateSet.docs.map((doc, i) => (
                                            <button
                                                key={doc.id}
                                                className={`flex-shrink-0 px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${previewDocIndex === i
                                                    ? 'bg-orange-500 text-white'
                                                    : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-700'
                                                    }`}
                                                onClick={() => setPreviewDocIndex(i)}
                                            >
                                                {doc.title || `Document ${i + 1}`}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="flex-1 overflow-hidden">
                                <DocPreviewPanel
                                    templateDoc={previewTemplateSet.docs[previewDocIndex]}
                                    formValues={previewTemplateSet.docs[previewDocIndex]?.previewValues || {}}
                                    placeholders={Object.fromEntries(
                                        (previewTemplateSet.placeholders || []).map(p => [p.slug, p])
                                    )}
                                    hideExportToggle
                                    sideBySide
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center py-12">
                            <p className="text-sm text-zinc-500 dark:text-zinc-400">{t('templateSets.noDocuments') || 'No documents in this template set.'}</p>
                        </div>
                    )}
                </div>
            )}

            <PreviewValuesModal
                opened={!!previewValuesTarget}
                templateSet={previewValuesTarget}
                onClose={() => setPreviewValuesTarget(null)}
                onSaved={(savedDocs) => {
                    setTemplateSets(prev => prev.map(ts => {
                        if (ts.id !== previewValuesTarget?.id) return ts;
                        return {
                            ...ts,
                            docs: (ts.docs || []).map(d =>
                                savedDocs[d.id] !== undefined
                                    ? { ...d, previewValues: savedDocs[d.id] }
                                    : d
                            ),
                        };
                    }));
                }}
            />

            {/* Delete Document Confirmation */}
            <ConfirmDialog
                open={!!confirmDoc}
                onClose={() => setConfirmDoc(null)}
                onConfirm={() => {
                    handleDeleteTemplateDoc(confirmDoc.templateSet, confirmDoc.doc);
                    setConfirmDoc(null);
                }}
                title="Delete Document"
                description={`Delete "${confirmDoc?.doc?.title}"? This cannot be undone.`}
            />
        </>
    );
}
